import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTemplate } from "@/contexts/TemplateContext";
import type { PublicProfile } from "@/types";
import { Gallery } from "@/components/gallery";
import { BookingModal } from "@/components/booking/BookingModal";

interface TutorTeacherTemplateProps {
  profile: PublicProfile;
}

export function TutorTeacherTemplate({ profile }: TutorTeacherTemplateProps) {
  const { t } = useTranslation("public");
  const { colors, typography } = useTemplate();
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  return (
    <div
      className="min-h-screen"
      style={{
        fontFamily: typography.body,
        backgroundColor: colors.colors.background,
        color: colors.colors.text,
      }}
    >
      {/* Navigation */}
      <nav className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ backgroundColor: colors.colors.primary }}
              >
                <i className="fas fa-graduation-cap text-white text-xl" />
              </div>
              <div>
                <h1
                  className="text-xl font-bold"
                  style={{ color: colors.colors.primary }}
                >
                  {profile.business_name}
                </h1>
                {profile.tagline && (
                  <p
                    className="text-xs uppercase tracking-widest"
                    style={{ color: colors.colors.accent }}
                  >
                    {profile.tagline}
                  </p>
                )}
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a
                href="#about"
                className="transition-colors cursor-pointer"
                style={{ color: colors.colors.textMuted }}
              >
                {t("template.tutor.about")}
              </a>
              <a
                href="#services"
                className="transition-colors cursor-pointer"
                style={{ color: colors.colors.textMuted }}
              >
                {t("template.tutor.services")}
              </a>
              <a
                href="#testimonials"
                className="transition-colors cursor-pointer"
                style={{ color: colors.colors.textMuted }}
              >
                {t("template.tutor.reviews")}
              </a>
              <a
                href="#contact"
                className="transition-colors cursor-pointer"
                style={{ color: colors.colors.textMuted }}
              >
                {t("template.tutor.contact")}
              </a>
              <button
                onClick={() => setIsBookingOpen(true)}
                className="px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer"
                style={{
                  backgroundColor: colors.colors.primary,
                  color: colors.colors.secondary,
                }}
              >
                {t("template.tutor.bookSession")}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section
        className="relative py-20 overflow-hidden"
        style={{ backgroundColor: colors.colors.primary }}
      >
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute top-20 left-20 w-80 h-80 rounded-full blur-3xl"
            style={{ backgroundColor: colors.colors.accent }}
          />
          <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full blur-3xl bg-white" />
        </div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p
                className="uppercase tracking-widest mb-4 text-sm font-semibold"
                style={{ color: colors.colors.accent }}
              >
                {t("template.tutor.expertAcademicTutoring")}
              </p>
              <h1
                className="text-5xl md:text-6xl font-bold text-white leading-tight mb-6"
                style={{ fontFamily: typography.heading }}
              >
                {t("template.tutor.unlockYourAcademicPotential")}
              </h1>
              <p
                className="text-xl mb-8 leading-relaxed"
                style={{ color: "rgba(255,255,255,0.8)" }}
              >
                {t("template.tutor.personalizedOneOnOne")}
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#contact"
                  className="inline-flex items-center px-8 py-4 rounded-full font-bold transition-all shadow-lg"
                  style={{
                    backgroundColor: colors.colors.accent,
                    color: colors.colors.primary,
                  }}
                >
                  {t("template.tutor.bookFreeConsultation")}
                </a>
                <a
                  href="#services"
                  className="inline-flex items-center px-8 py-4 border-2 border-white text-white rounded-full font-bold transition-all"
                >
                  {t("template.tutor.viewServices")}
                </a>
              </div>
              <div className="flex items-center space-x-8 mt-12">
                <div>
                  <p
                    className="text-4xl font-bold"
                    style={{ color: colors.colors.accent }}
                  >
                    98%
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    {t("template.tutor.improvementRate")}
                  </p>
                </div>
                <div>
                  <p
                    className="text-4xl font-bold"
                    style={{ color: colors.colors.accent }}
                  >
                    15+
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    {t("template.tutor.yearsExperience")}
                  </p>
                </div>
                <div>
                  <p
                    className="text-4xl font-bold"
                    style={{ color: colors.colors.accent }}
                  >
                    500+
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: "rgba(255,255,255,0.6)" }}
                  >
                    {t("template.tutor.studentsTaught")}
                  </p>
                </div>
              </div>
            </div>
            <div className="relative">
              {profile.profile_image ? (
                <img
                  src={profile.profile_image}
                  alt={profile.business_name}
                  className="rounded-3xl shadow-2xl w-full object-cover"
                />
              ) : (
                <div
                  className="rounded-3xl shadow-2xl w-full aspect-square flex items-center justify-center"
                  style={{ backgroundColor: colors.colors.secondary }}
                >
                  <span
                    className="text-6xl font-bold"
                    style={{
                      fontFamily: typography.heading,
                      color: colors.colors.primary,
                    }}
                  >
                    {profile.business_name[0]}
                  </span>
                </div>
              )}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-6">
                <div className="flex items-center space-x-3">
                  <i
                    className="fas fa-star text-2xl"
                    style={{ color: colors.colors.accent }}
                  />
                  <div>
                    <p
                      className="text-3xl font-bold"
                      style={{ color: colors.colors.primary }}
                    >
                      5.0
                    </p>
                    <p
                      className="text-sm"
                      style={{ color: colors.colors.textMuted }}
                    >
                      {t("template.tutor.parentRating")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      {profile.bio && (
        <section id="about" className="py-24 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="relative">
                {profile.profile_image ? (
                  <img
                    src={profile.profile_image}
                    alt={profile.business_name}
                    className="rounded-3xl shadow-2xl w-full object-cover"
                  />
                ) : (
                  <div
                    className="rounded-3xl shadow-2xl w-full aspect-[6/7] flex items-center justify-center"
                    style={{ backgroundColor: colors.colors.secondary }}
                  >
                    <span
                      className="text-6xl font-bold"
                      style={{
                        fontFamily: typography.heading,
                        color: colors.colors.primary,
                      }}
                    >
                      {profile.business_name[0]}
                    </span>
                  </div>
                )}
                <div
                  className="absolute -bottom-6 -right-6 rounded-2xl p-6 shadow-xl"
                  style={{
                    backgroundColor: colors.colors.primary,
                    color: colors.colors.secondary,
                  }}
                >
                  <i
                    className="fas fa-award text-2xl mb-2"
                    style={{ color: colors.colors.accent }}
                  />
                  <p className="font-bold">
                    {t("template.tutor.formerProfessor")}
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: colors.colors.secondary, opacity: 0.7 }}
                  >
                    {t("template.tutor.uclaDepartment")}
                  </p>
                </div>
              </div>
              <div
                className="rounded-xl p-5"
                style={{ backgroundColor: colors.colors.background }}
              >
                <i
                  className="fas fa-book text-2xl mb-3"
                  style={{ color: colors.colors.primary }}
                />
                <h4
                  className="font-bold"
                  style={{ color: colors.colors.primary }}
                >
                  {t("template.tutor.publishedAuthor")}
                </h4>
                <p
                  className="text-sm"
                  style={{ color: colors.colors.textMuted }}
                >
                  {t("template.tutor.educationResearch")}
                </p>
              </div>
            </div>
            <div>
              <p
                className="uppercase tracking-widest mb-4 text-sm font-semibold"
                style={{ color: colors.colors.accent }}
              >
                {t("template.tutor.aboutMe")}
              </p>
              <h2
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{
                  fontFamily: typography.heading,
                  color: colors.colors.primary,
                }}
              >
                {t("template.tutor.meetYourTutor")}
              </h2>
              <p
                className="text-lg mb-6 leading-relaxed"
                style={{ color: colors.colors.textMuted }}
              >
                {profile.bio}
              </p>
              {profile.location && (
                <p
                  className="text-sm mb-6"
                  style={{ color: colors.colors.textMuted }}
                >
                  📍 {profile.location}
                </p>
              )}
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div
                  className="rounded-xl p-5"
                  style={{ backgroundColor: colors.colors.background }}
                >
                  <i
                    className="fas fa-user-graduate text-2xl mb-3"
                    style={{ color: colors.colors.primary }}
                  />
                  <h4
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    Former Professor
                  </h4>
                  <p
                    className="text-sm"
                    style={{ color: colors.colors.textMuted }}
                  >
                    UCLA Department
                  </p>
                </div>
                <div
                  className="rounded-xl p-5"
                  style={{ backgroundColor: colors.colors.background }}
                >
                  <i
                    className="fas fa-book text-2xl mb-3"
                    style={{ color: colors.colors.primary }}
                  />
                  <h4
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    Published Author
                  </h4>
                  <p
                    className="text-sm"
                    style={{ color: colors.colors.textMuted }}
                  >
                    Education Research
                  </p>
                </div>
              </div>
              {profile.social_links && profile.social_links.length > 0 && (
                <div className="flex items-center space-x-4">
                  {profile.social_links.slice(0, 3).map((link) => (
                    <a
                      key={link.platform}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-all"
                      style={{
                        backgroundColor: colors.colors.primary,
                        color: colors.colors.accent,
                      }}
                    >
                      <i className={`fab fa-${link.platform}`} />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Services */}
      {profile.services && profile.services.length > 0 && (
        <section
          id="services"
          className="py-24 px-4"
          style={{ backgroundColor: colors.colors.background }}
        >
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <p
                className="uppercase tracking-widest mb-4 text-sm font-semibold"
                style={{ color: colors.colors.accent }}
              >
                {t("template.tutor.services")}
              </p>
              <h2
                className="text-4xl md:text-5xl font-bold"
                style={{
                  fontFamily: typography.heading,
                  color: colors.colors.primary,
                }}
              >
                {t("template.tutor.tutoringAndPricing")}
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {profile.services.map((service, index) => (
                <div
                  key={service.id}
                  className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow"
                >
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? `${colors.colors.primary}10`
                          : `${colors.colors.accent}20`,
                    }}
                  >
                    <i
                      className={`fas fa-${
                        index === 0
                          ? "calculator"
                          : index === 1
                            ? "flask"
                            : index === 2
                              ? "file-alt"
                              : index === 3
                                ? "pencil-alt"
                                : index === 4
                                  ? "laptop-code"
                                  : "users"
                      } text-2xl`}
                      style={{
                        color:
                          index % 2 === 0
                            ? colors.colors.primary
                            : colors.colors.accent,
                      }}
                    />
                  </div>
                  <div className="flex justify-between items-start mb-4">
                    <h3
                      className="text-xl font-bold"
                      style={{ color: colors.colors.primary }}
                    >
                      {service.name}
                    </h3>
                    {service.price_display !== "hidden" && service.price && (
                      <span
                        className="text-2xl font-bold"
                        style={{ color: colors.colors.accent }}
                      >
                        ${service.price.toFixed(0)}
                        <span
                          className="text-sm"
                          style={{ color: colors.colors.textMuted }}
                        >
                          /hr
                        </span>
                      </span>
                    )}
                  </div>
                  {service.description && (
                    <p
                      className="mb-4"
                      style={{ color: colors.colors.textMuted }}
                    >
                      {service.description}
                    </p>
                  )}
                  {service.duration_minutes && (
                    <div
                      className="flex items-center text-sm"
                      style={{ color: colors.colors.textMuted }}
                    >
                      <i
                        className="fas fa-clock mr-2"
                        style={{ color: colors.colors.primary }}
                      />
                      <span>{service.duration_minutes} minute sessions</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <section id="testimonials" className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p
              className="uppercase tracking-widest mb-4 text-sm font-semibold"
              style={{ color: colors.colors.accent }}
            >
              {t("template.tutor.testimonials")}
            </p>
            <h2
              className="text-4xl md:text-5xl font-bold"
              style={{
                fontFamily: typography.heading,
                color: colors.colors.primary,
              }}
            >
              {t("template.tutor.whatParentsSay")}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div
              className="rounded-2xl p-8"
              style={{ backgroundColor: colors.colors.background }}
            >
              <div
                className="flex mb-4"
                style={{ color: colors.colors.accent }}
              >
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
              </div>
              <p
                className="mb-6 leading-relaxed"
                style={{ color: colors.colors.textMuted }}
              >
                "Dr. Lee helped my daughter go from a C to an A in AP Calculus.
                Her patient teaching style made all the difference. Highly
                recommend!"
              </p>
              <div className="flex items-center space-x-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${colors.colors.primary}10` }}
                >
                  <span
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    JM
                  </span>
                </div>
                <div>
                  <p
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    Jennifer M.
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: colors.colors.textMuted }}
                  >
                    Parent of 11th Grader
                  </p>
                </div>
              </div>
            </div>
            <div
              className="rounded-2xl p-8"
              style={{ backgroundColor: colors.colors.background }}
            >
              <div
                className="flex mb-4"
                style={{ color: colors.colors.accent }}
              >
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
              </div>
              <p
                className="mb-6 leading-relaxed"
                style={{ color: colors.colors.textMuted }}
              >
                "My son improved his SAT score by 200 points after just 8
                sessions with Dr. Lee. She knows exactly how to teach to the
                test."
              </p>
              <div className="flex items-center space-x-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${colors.colors.primary}10` }}
                >
                  <span
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    RK
                  </span>
                </div>
                <div>
                  <p
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    Robert K.
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: colors.colors.textMuted }}
                  >
                    Parent of 12th Grader
                  </p>
                </div>
              </div>
            </div>
            <div
              className="rounded-2xl p-8"
              style={{ backgroundColor: colors.colors.background }}
            >
              <div
                className="flex mb-4"
                style={{ color: colors.colors.accent }}
              >
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
                <i className="fas fa-star" />
              </div>
              <p
                className="mb-6 leading-relaxed"
                style={{ color: colors.colors.textMuted }}
              >
                "As a college student, I struggled with statistics. Dr. Lee's
                explanations made complex concepts finally click. Got an A in
                the course!"
              </p>
              <div className="flex items-center space-x-3">
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${colors.colors.primary}10` }}
                >
                  <span
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    AT
                  </span>
                </div>
                <div>
                  <p
                    className="font-bold"
                    style={{ color: colors.colors.primary }}
                  >
                    Amy T.
                  </p>
                  <p
                    className="text-sm"
                    style={{ color: colors.colors.textMuted }}
                  >
                    College Student, UCLA
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {profile.gallery && profile.gallery.length > 0 && (
        <section className="py-24 px-4 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p
                className="uppercase tracking-widest mb-4 text-sm font-semibold"
                style={{ color: colors.colors.accent }}
              >
                {t("template.tutor.gallery")}
              </p>
              <h2
                className="text-4xl md:text-5xl font-bold"
                style={{
                  fontFamily: typography.heading,
                  color: colors.colors.primary,
                }}
              >
                {t("template.tutor.myWork")}
              </h2>
            </div>
            <Gallery
              images={profile.gallery}
              layout="grid"
              limit={6}
              enableLightbox
              showCaptions
              aspectRatio="square"
              gapSize={4}
            />
          </div>
        </section>
      )}

      {/* Hours / Availability */}
      {profile.availability && profile.availability.length > 0 && (
        <section
          className="py-24 px-4"
          style={{ backgroundColor: colors.colors.background }}
        >
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <p
                className="uppercase tracking-widest mb-4 text-sm font-semibold"
                style={{ color: colors.colors.accent }}
              >
                {t("template.tutor.availability")}
              </p>
              <h2
                className="text-4xl md:text-5xl font-bold"
                style={{
                  fontFamily: typography.heading,
                  color: colors.colors.primary,
                }}
              >
                {t("template.tutor.sessionHours")}
              </h2>
            </div>
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  {profile.availability.slice(0, 4).map((day) => (
                    <div
                      key={day.day_of_week}
                      className="flex justify-between items-center py-3 border-b"
                      style={{ borderColor: colors.colors.background }}
                    >
                      <span
                        className="font-bold"
                        style={{ color: colors.colors.primary }}
                      >
                        {day.day_name}
                      </span>
                      <span style={{ color: colors.colors.textMuted }}>
                        {day.is_available
                          ? day.is_all_day
                            ? t("template.common.allDay")
                            : `${day.start_time} - ${day.end_time}`
                          : t("template.common.closed")}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="space-y-4">
                  {profile.availability.slice(4).map((day) => (
                    <div
                      key={day.day_of_week}
                      className="flex justify-between items-center py-3 border-b"
                      style={{ borderColor: colors.colors.background }}
                    >
                      <span
                        className="font-bold"
                        style={{ color: colors.colors.primary }}
                      >
                        {day.day_name}
                      </span>
                      <span
                        style={{
                          color: colors.colors.accent,
                          fontWeight: "bold",
                        }}
                      >
                        {day.is_available
                          ? day.is_all_day
                            ? t("template.common.allDay")
                            : `${day.start_time} - ${day.end_time}`
                          : t("template.common.byAppointment")}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div
                className="mt-8 text-center rounded-xl p-6"
                style={{ backgroundColor: `${colors.colors.primary}5` }}
              >
                <p style={{ color: colors.colors.textMuted }}>
                  <i
                    className="fas fa-info-circle mr-2"
                    style={{ color: colors.colors.accent }}
                  />
                  <strong>{t("template.tutor.onlineSessionsAvailable")}</strong>
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contact */}
      <section id="contact" className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <p
                className="uppercase tracking-widest mb-4 text-sm font-semibold"
                style={{ color: colors.colors.accent }}
              >
                {t("template.tutor.contact")}
              </p>
              <h2
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{
                  fontFamily: typography.heading,
                  color: colors.colors.primary,
                }}
              >
                {t("template.tutor.letsStartLearning")}
              </h2>
              <p
                className="text-lg mb-8 leading-relaxed"
                style={{ color: colors.colors.textMuted }}
              >
                {t("template.tutor.free30MinuteConsultation")}
              </p>
              <div className="space-y-6">
                {profile.email && (
                  <div className="flex items-start space-x-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${colors.colors.primary}10` }}
                    >
                      <i
                        className="fas fa-envelope text-xl"
                        style={{ color: colors.colors.primary }}
                      />
                    </div>
                    <div>
                      <h4
                        className="font-bold"
                        style={{ color: colors.colors.primary }}
                      >
                        {t("template.tutor.email")}
                      </h4>
                      <p style={{ color: colors.colors.textMuted }}>
                        {profile.email}
                      </p>
                    </div>
                  </div>
                )}
                {profile.phone && (
                  <div className="flex items-start space-x-4">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${colors.colors.primary}10` }}
                    >
                      <i
                        className="fas fa-phone text-xl"
                        style={{ color: colors.colors.primary }}
                      />
                    </div>
                    <div>
                      <h4
                        className="font-bold"
                        style={{ color: colors.colors.primary }}
                      >
                        {t("template.tutor.phone")}
                      </h4>
                      <p style={{ color: colors.colors.textMuted }}>
                        {profile.phone}
                      </p>
                    </div>
                  </div>
                )}
                <div className="flex items-start space-x-4">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${colors.colors.primary}10` }}
                  >
                    <i
                      className="fas fa-video text-xl"
                      style={{ color: colors.colors.primary }}
                    />
                  </div>
                  <div>
                    <h4
                      className="font-bold"
                      style={{ color: colors.colors.primary }}
                    >
                      {t("template.tutor.onlineSessions")}
                    </h4>
                    <p style={{ color: colors.colors.textMuted }}>
                      {t("template.tutor.zoomFacetimeGoogleMeet")}
                    </p>
                  </div>
                </div>
              </div>
              {profile.social_links && profile.social_links.length > 0 && (
                <div className="mt-8">
                  <h4
                    className="font-bold mb-4"
                    style={{ color: colors.colors.primary }}
                  >
                    {t("template.tutor.followUpdates")}
                  </h4>
                  <div className="flex space-x-4">
                    {profile.social_links.slice(0, 3).map((link) => (
                      <a
                        key={link.platform}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-xl flex items-center justify-center transition-all"
                        style={{
                          backgroundColor: colors.colors.primary,
                          color: colors.colors.accent,
                        }}
                      >
                        <i className={`fab fa-${link.platform} text-xl`} />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div>
              <form
                className="rounded-3xl p-8 shadow-xl"
                style={{ backgroundColor: colors.colors.background }}
              >
                <h3
                  className="text-2xl font-bold mb-6"
                  style={{
                    fontFamily: typography.heading,
                    color: colors.colors.primary,
                  }}
                >
                  {t("template.tutor.requestFreeConsultation")}
                </h3>
                <div className="space-y-5">
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.colors.primary }}
                    >
                      {t("template.tutor.parentStudentName")}
                    </label>
                    <input
                      type="text"
                      className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                      style={{
                        borderColor: "white",
                        backgroundColor: "white",
                      }}
                      placeholder={t("template.tutor.yourName")}
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.colors.primary }}
                    >
                      {t("template.tutor.email")}
                    </label>
                    <input
                      type="email"
                      className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                      style={{
                        borderColor: "white",
                        backgroundColor: "white",
                      }}
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.colors.primary }}
                    >
                      {t("template.tutor.phone")}
                    </label>
                    <input
                      type="tel"
                      className="w-full px-4 py-4 rounded-xl border outline-none transition-all"
                      style={{
                        borderColor: "white",
                        backgroundColor: "white",
                      }}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.colors.primary }}
                    >
                      {t("template.tutor.gradeLevel")}
                    </label>
                    <select
                      className="w-full px-4 py-4 rounded-xl border outline-none transition-all cursor-pointer"
                      style={{
                        borderColor: "white",
                        backgroundColor: "white",
                      }}
                    >
                      <option value="">
                        {t("template.tutor.selectGradeLevel")}
                      </option>
                      <option>{t("template.tutor.middleSchool")}</option>
                      <option>{t("template.tutor.highSchool")}</option>
                      <option>{t("template.tutor.collegeUniversity")}</option>
                      <option>{t("template.tutor.adultLearner")}</option>
                    </select>
                  </div>
                  <div>
                    <label
                      className="block text-sm font-medium mb-2"
                      style={{ color: colors.colors.primary }}
                    >
                      {t("template.tutor.goalsAndQuestions")}
                    </label>
                    <textarea
                      rows={4}
                      className="w-full px-4 py-4 rounded-xl border outline-none transition-all resize-none"
                      style={{
                        borderColor: "white",
                        backgroundColor: "white",
                      }}
                      placeholder={t(
                        "template.tutor.tellMeAboutYourAcademicGoals",
                      )}
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-4 rounded-xl font-semibold transition-all"
                    style={{
                      backgroundColor: colors.colors.primary,
                      color: colors.colors.secondary,
                    }}
                  >
                    {t("template.tutor.requestFreeConsultation")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="py-12 px-4 text-center"
        style={{
          backgroundColor: colors.colors.primary,
          color: colors.colors.secondary,
        }}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ backgroundColor: colors.colors.accent }}
        >
          <i
            className="fas fa-graduation-cap text-2xl"
            style={{ color: colors.colors.primary }}
          />
        </div>
        <h3
          className="text-2xl font-bold mb-2"
          style={{ fontFamily: typography.heading }}
        >
          {profile.business_name}
        </h3>
        <p
          className="mb-6"
          style={{ color: colors.colors.secondary, opacity: 0.7 }}
        >
          {t("template.tutor.academicTutoringAndTestPrep")}
        </p>
        {profile.social_links && profile.social_links.length > 0 && (
          <div className="flex justify-center space-x-4 mb-6">
            {profile.social_links.slice(0, 3).map((link) => (
              <a
                key={link.platform}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
                style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              >
                <i className={`fab fa-${link.platform}`} />
              </a>
            ))}
          </div>
        )}
        <p
          className="text-sm"
          style={{ color: colors.colors.secondary, opacity: 0.5 }}
        >
          © {new Date().getFullYear()} {profile.business_name}.{" "}
          {t("template.tutor.allRightsReserved")}
        </p>
      </footer>

      {isBookingOpen && profile.services && profile.services.length > 0 && (
        <BookingModal
          isOpen={isBookingOpen}
          onClose={() => setIsBookingOpen(false)}
          profile={profile}
          services={profile.services}
          categories={[]}
        />
      )}
    </div>
  );
}

export default TutorTeacherTemplate;
